import { Container, Graphics, Text } from "pixi.js";
import { worldToScreen } from "./geometry";
import type { Point } from "./geometry";
import { navigationArrow, navigationSafeRect } from "./navigation-layout";

export interface NavigationTarget extends Point {
  name: string;
  kind: "objective" | "hostile" | "station";
  entityVisible?: boolean;
}

export class NavigationMarkers {
  readonly world = new Container();
  readonly screen = new Container();
  private readonly pin = new Graphics();
  private readonly arrow = new Graphics();
  private readonly pinLabel = new Text({
    text: "",
    style: {
      fontFamily: "system-ui, sans-serif",
      fontSize: 12,
      fill: 0xf5ce83,
      letterSpacing: 0.5,
      wordWrap: true,
      wordWrapWidth: 220,
      align: "center",
    },
  });
  private readonly edgeLabel = new Text({
    text: "",
    style: {
      fontFamily: "system-ui, sans-serif",
      fontSize: 11,
      fill: 0xf5ce83,
      letterSpacing: 0.5,
      wordWrap: true,
      wordWrapWidth: 180,
      align: "center",
    },
  });

  constructor() {
    this.world.label = "navigation-world";
    this.screen.label = "navigation-screen";
    this.edgeLabel.label = "navigation-edge-label";
    this.pinLabel.anchor.set(0.5);
    this.pinLabel.y = -43;
    this.edgeLabel.anchor.set(0.5);
    this.world.addChild(this.pin, this.pinLabel);
    this.screen.addChild(this.arrow, this.edgeLabel);
  }

  render(
    target: NavigationTarget | undefined,
    camera: Point,
    width: number,
    height: number,
    zoom: number,
    tutorialGuidanceVisible = false,
  ): void {
    this.world.visible = target !== undefined;
    this.screen.visible = false;
    if (!target) return;
    const color =
      target.kind === "hostile"
        ? 0xff967f
        : target.kind === "station"
          ? 0x83d8df
          : 0xf5ce83;
    const distance = Math.round(
      Math.hypot(target.x - camera.x, target.y - camera.y),
    );
    this.world.position.set(target.x, target.y);
    this.world.scale.set(1 / zoom);
    this.pin.clear();
    this.pin
      .poly([-6, -28, 0, -22, 6, -28, 0, -34])
      .stroke({ color, width: 1.5 });
    this.pinLabel.text = `${target.name} · ${distance} м`;
    this.pinLabel.style.fill = color;
    const point = worldToScreen(target, camera, width, height, zoom);
    const rect = navigationSafeRect(width, height, tutorialGuidanceVisible);
    const pinHalfWidth = Math.max(24, this.pinLabel.width / 2);
    const pinTop = Math.min(-34, -43 - this.pinLabel.height / 2);
    if (
      point.x - pinHalfWidth > rect.left &&
      point.x + pinHalfWidth < rect.right &&
      point.y + pinTop > rect.top &&
      point.y + 24 < rect.bottom
    ) {
      if (target.entityVisible) this.world.visible = false;
      return;
    }
    this.world.visible = false;
    this.screen.visible = true;
    const { x, y, angle } = navigationArrow(point, width, height, rect);
    this.arrow
      .clear()
      .poly([
        x + Math.cos(angle) * 10,
        y + Math.sin(angle) * 10,
        x + Math.cos(angle + 2.3) * 8,
        y + Math.sin(angle + 2.3) * 8,
        x + Math.cos(angle - 2.3) * 8,
        y + Math.sin(angle - 2.3) * 8,
      ])
      .fill({ color, alpha: 0.95 });
    this.edgeLabel.text = `${target.name} · ${distance} м`;
    this.edgeLabel.style.fill = color;
    const halfWidth = this.edgeLabel.width / 2;
    const halfHeight = this.edgeLabel.height / 2;
    this.edgeLabel.position.set(
      Math.max(
        rect.left + halfWidth,
        Math.min(rect.right - halfWidth, x - Math.cos(angle) * 42),
      ),
      Math.max(
        rect.top + halfHeight,
        Math.min(rect.bottom - halfHeight, y - Math.sin(angle) * 32),
      ),
    );
  }

  destroy(): void {
    this.world.destroy({ children: true });
    this.screen.destroy({ children: true });
  }
}
